import { toastError, toastSuccess } from '@components/Toast/Toast';
import { useBankProfile } from '@contexts/BankProfileContext';
import { useAuth } from '@hooks/useAuth';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface SendMessageParams {
	content: { text: string };
	attachments?: File[];
	signature: string;
}

export const useUpdateThread = (threadId: string, onSuccess: () => void) => {
	const { session } = useAuth();
	const { selectedProfile } = useBankProfile();
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const updateMessageThread = async ({
		content,
		attachments,
		signature,
	}: SendMessageParams) => {
		const formData = new FormData();
		formData.append('content[text]', content.text);

		if (attachments) {
			attachments.forEach((file, index) => {
				formData.append(`attachments[${index}]`, file);
			});
		}

		const { data } = await axios.post(
			`${import.meta.env.VITE_API_HOST}/api/v1/profiles/${selectedProfile.id}/threads/${threadId}/messages`,
			formData,
			{
				headers: {
					Authorization: session?.tokenized,
					'X-Signature': signature,
				},
			},
		);

		return data;
	};

	return useMutation({
		mutationFn: updateMessageThread,
		onSuccess: () => {
			onSuccess();
			queryClient.invalidateQueries({
				queryKey: ['thread', selectedProfile?.id, threadId, session?.tokenized],
			});
			toastSuccess(t('tfa.alerts.messageSigned'));
		},
		onError: async () => {
			toastError(t('tfa.alerts.failed'));
		},
	});
};
